import { Box, styled } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAccount, useChat, useToast } from "../../../context";
import {
  useMessageGrouping,
  useMessageHandler,
  useMessageReadStatus,
} from "../../../hooks";
import { useChatScroll } from "../../../hooks/useChatScroll";
import { MessageType } from "../../../interfaces/all";
import { getMessagesByClientPhone } from "../../../services/api";
import socketService from "../../../services/socket";
import { normalizePhoneNumber } from "../../../utils/phone-utils";
import EmptyState from "./EmptyState";
import LoadingStateComponent from "./LoadingState";
import MessageGroup from "./MessageGroup";

const urlImageWrapper =
  "https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png";

const Wrapper = styled(Box)`
  background-image: url(${urlImageWrapper});
  background-size: 50%;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: calc(100% - 60px); // Altura total menos o ChatInput
  overflow-y: scroll !important; // Forçando o scroll a aparecer
  padding: 20px 0;
  position: relative;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    width: 6px;
    display: block !important;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    min-height: 40px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const transformMessage = (message: MessageType) => {
  return {
    _id: message._id,
    type: message.type,
    sentBy: message.sentBy,
    content: {
      ...message.content,
      type:
        message.type === "image_text"
          ? "image"
          : message.type === "button_text"
            ? "text"
            : (message.type as
                | "text"
                | "image"
                | "file"
                | "voice"
                | "emoji"
                | "template"
                | "reaction"
                | "unsupported"),
      voiceData:
        message.content.voiceData ||
        (message as any).content.voice ||
        (message.content as any).voice,
      imageData: message.content.imageData || (message.content as any).image,
    },
    timestamp: (() => {
      const ts = message.timestamp;
      if (ts instanceof Date) return ts;
      if (typeof ts === "number") return new Date(ts);
      let dateStr = String(ts);
      if (
        dateStr.includes("T") &&
        !dateStr.includes("Z") &&
        !dateStr.includes("+") &&
        !/-\d{2}:\d{2}$/.test(dateStr)
      ) {
        dateStr += "Z";
      }
      return new Date(dateStr);
    })(),
    statuses: message.statuses,
    readStatus: message.readStatus,
    isFromMe: message.sentBy === "client",
  };
};

interface ChatMessagesProps {
  onAllRead?: () => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ onAllRead }) => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conversationScrollTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const lastMessagesLengthRef = useRef(0);
  const lastConversationIdRef = useRef<string | null>(null);

  const { selectedConversation } = useChat();
  const { socket } = useAccount();
  const { showError } = useToast();
  const { account } = useAccount();
  const isMountedRef = useRef(true);

  // useMessageHandler com suporte a onMessageUpdate
  const messageHandler = useMessageHandler({
    socket,
    selectedConversation,
    onNewMessage: useCallback(() => {
      // A marcação como lida será feita no onMessagesUpdate após a mensagem ser adicionada ao estado
    }, []),
    onMessagesUpdate: useCallback(
      (updatedMessages: MessageType[]) => {
        // Atualizar imediatamente o hook useMessageReadStatus
        if (updateMessagesRef.current) {
          updateMessagesRef.current(updatedMessages);

          // Só marcar como lida se a conversa estiver selecionada
          if (selectedConversation) {
            // A marcação automática agora é feita via markAllAsRead (delay 2s) em um useEffect separado
            // para evitar loops infinitos de re-renderização e flicker visual.
            if (selectedConversation) {
              // Sincronização em background se necessário
            }
          }
        }
      },
      [selectedConversation],
    ),
  });
  const messages = messageHandler.messages;
  const setMessages = messageHandler.setMessages;

  // Usar o hook de scroll
  const {
    messageState,
    setMessageState,
    loadMoreRef,
    wrapperRef,
    messagesEndRef,
    isLoading,
  } = useChatScroll({
    selectedConversation,
    onError: showError,
    onLoadMessages: useCallback(
      async (phone: string, page = 1, append = false) => {
        try {
          // Validar se o phone não está vazio
          if (!phone || phone.trim() === "") {
            console.warn("[ChatMessages] Phone vazio ou inválido:", phone);
            if (!append) {
              setMessages([]);
            }
            setMessageState((prev) => ({
              ...prev,
              loadingState: "idle",
            }));
            return;
          }

          const response = await getMessagesByClientPhone(phone, page, 50);

          if (response.status === "success" && "data" in response) {
            // NOVA ESTRUTURA: response.data agora é um objeto com paginação
            let newMessages: MessageType[] = [];
            let hasMoreMessages = false;

            if (response.data && typeof response.data === "object") {
              // Verificar se é a nova estrutura paginada
              if (
                "messages" in response.data &&
                Array.isArray(response.data.messages)
              ) {
                newMessages = response.data.messages as MessageType[];
                // Verificar se tem as propriedades da estrutura paginada
                const paginatedData = response.data as any;
                hasMoreMessages = paginatedData.hasMore || false;
              } else if (Array.isArray(response.data)) {
                newMessages = response.data as MessageType[];
                hasMoreMessages = false;
              }
            }

            if (append) {
              // Adicionar mensagens antigas no início (para paginação)
              setMessages((prev) => {
                // Remover duplicatas baseado no _id
                const existingIds = new Set(prev.map((msg) => msg._id));
                const uniqueNewMessages = newMessages.filter(
                  (msg) => !existingIds.has(msg._id),
                );

                if (uniqueNewMessages.length === 0) {
                  return prev;
                }

                // Concatenar mensagens antigas no início
                const combinedMessages = [...uniqueNewMessages, ...prev];

                // Ordenar por timestamp (mais antigas primeiro)
                const sortedMessages = combinedMessages.sort((a, b) => {
                  const timeA = new Date(a.timestamp).getTime();
                  const timeB = new Date(b.timestamp).getTime();
                  return timeA - timeB;
                });

                return sortedMessages;
              });
            } else {
              // Substituir mensagens (carregamento inicial)
              setMessages(newMessages);
            }

            // Atualizar estado de carregamento e hasMore
            setMessageState((prev) => ({
              ...prev,
              loadingState: "idle",
              hasMore: hasMoreMessages && newMessages.length > 0,
            }));
          } else {
            console.error("[ChatMessages] Erro na resposta da API:", response);
            if (!append) {
              setMessages([]);
            }
            setMessageState((prev) => ({
              ...prev,
              loadingState: "idle",
              hasMore: false,
            }));
          }
        } catch (error: any) {
          console.error("[ChatMessages] ERRO NO CATCH:", error);
          if (!append) {
            setMessages([]);
          }
          setMessageState((prev) => ({
            ...prev,
            loadingState: "idle",
            hasMore: false,
          }));
          // Usar o sistema centralizado de erro
          showError("Erro ao carregar mensagens. Tente novamente.");
        }
      },
      [],
    ),
  });

  // Criar um ref para updateMessages para poder chamá-lo imediatamente
  const updateMessagesRef = useRef<
    ((messages: MessageType[]) => void) | undefined
  >(undefined);

  // useMessageReadStatus COM onMessageUpdate para atualizar o ícone em tempo real
  const {
    markAllAsRead,
    markAsRead,
    markAsReadDirect,
    updateMessages,
    getMessageReadStatus,
  } = useMessageReadStatus({
    socket,
    selectedConversation: selectedConversation,
    onMessageUpdate: (updatedMessage) => {
      if (updatedMessage._id) {
        setMessages((prev) => {
          const exists = prev.some((msg) => msg._id === updatedMessage._id);
          if (!exists) {
            return [...prev, { ...updatedMessage }];
          }

          const updated = prev.map((msg) =>
            msg._id === updatedMessage._id
              ? { ...msg, readStatus: updatedMessage.readStatus }
              : msg,
          );

          return updated;
        });

        // Forçar re-render imediatamente para atualizar o ícone
        requestAnimationFrame(() => {
          setForceUpdate((prev) => prev + 1);
        });
      }
    },
  });

  // Atualizar o ref quando updateMessages mudar
  useEffect(() => {
    updateMessagesRef.current = updateMessages;
  }, [updateMessages]);

  // Criar um ref para markAsRead para evitar problemas de referência
  const markAsReadRef = useRef(markAsRead);
  useEffect(() => {
    markAsReadRef.current = markAsRead;
  }, [markAsRead]);

  // Criar um ref para markAsRead direto (sem debounce) para marcação automática
  const markAsReadDirectRef = useRef<
    ((messageId: string) => Promise<void>) | undefined
  >(undefined);
  useEffect(() => {
    // Usar a função markAsRead direta do hook (sem debounce)
    markAsReadDirectRef.current = async (messageId: string) => {
      // Chamar a função markAsRead diretamente do hook
      if (markAsReadDirect) {
        await markAsReadDirect(messageId);
      }
    };
  }, [markAsReadDirect]);

  // Atualizar o useMessageReadStatus com as mensagens atuais
  useEffect(() => {
    if (isMountedRef.current) {
      updateMessages(messages);
    }
  }, [messages, updateMessages]);

  // Efeito adicional para sincronizar status quando mensagens são carregadas
  useEffect(() => {
    if (messages.length > 0 && selectedConversation && isMountedRef.current) {
      updateMessages(messages);
    }
  }, [messages, selectedConversation, updateMessages]);

  // --- SCROLL AUTOMÁTICO OTIMIZADO ---
  // Scroll SUAVE ao receber nova mensagem
  useEffect(() => {
    if (
      messages.length > lastMessagesLengthRef.current &&
      lastMessagesLengthRef.current > 0
    ) {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 80);
    }
    lastMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Scroll INSTANTÂNEO ao abrir nova conversa
  useEffect(() => {
    if (selectedConversation) {
      const conversationId = selectedConversation.id;

      // Verificar se é uma nova conversa
      if (lastConversationIdRef.current !== conversationId) {
        lastConversationIdRef.current = conversationId;

        // Limpar timeout anterior se existir
        if (conversationScrollTimeoutRef.current) {
          clearTimeout(conversationScrollTimeoutRef.current);
        }

        // Scroll instantâneo para a última mensagem quando clicar na conversa
        conversationScrollTimeoutRef.current = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
          }
        }, 150);
      }
    } else {
      lastConversationIdRef.current = null;
    }
  }, [selectedConversation?.id]);

  // Scroll adicional quando as mensagens são carregadas para uma nova conversa
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      const conversationId = selectedConversation.id;

      // Se é a primeira vez carregando mensagens para esta conversa
      if (lastConversationIdRef.current === conversationId) {
        if (conversationScrollTimeoutRef.current) {
          clearTimeout(conversationScrollTimeoutRef.current);
        }

        conversationScrollTimeoutRef.current = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
          }
        }, 200);
      }
    }
  }, [messages.length, selectedConversation?.id]);

  // Limpeza de timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (conversationScrollTimeoutRef.current)
        clearTimeout(conversationScrollTimeoutRef.current);
    };
  }, []);

  // Handler para atualização de status de mensagens enviadas pelo usuário
  const handleMessageStatusUpdateRef = useRef<
    | ((data: {
        messageId: string;
        to: string;
        status: string;
        timestamp: number;
        conversation: Record<string, unknown>;
        pricing: Record<string, unknown>;
        recipient_id: string;
        statusHistory: Array<Record<string, unknown>>;
        companyId: string;
        friendlyErrorMessage?: string;
      }) => void)
    | null
  >(null);

  // Atualizar o handler ref quando selectedConversation mudar
  useEffect(() => {
    handleMessageStatusUpdateRef.current = (data: {
      messageId: string;
      to: string;
      status: string;
      timestamp: number;
      conversation: any;
      pricing: any;
      recipient_id: string;
      statusHistory: any[];
      companyId: string;
      friendlyErrorMessage?: string;
    }) => {
      if (data.friendlyErrorMessage) {
        showError(data.friendlyErrorMessage);
      }

      const conversationPhone =
        selectedConversation?.valid_telephone ||
        selectedConversation?.telephone;

      // Verificar se a atualização é para a conversa atual
      if (conversationPhone === data.to) {
        setMessages((prev) => {
          const updated = prev.map((msg) => {
            // Verificar se a mensagem tem o WhatsApp ID correspondente
            // O messageId do evento é o ID do WhatsApp, não o _id do MongoDB
            const whatsappId = (msg.rawWebhookData as any)?.id;

            if (whatsappId === data.messageId) {
              const updatedMessage = {
                ...msg,
                statuses: data.statusHistory || [
                  ...(msg.statuses || []),
                  {
                    status: data.status,
                    timestamp: new Date(data.timestamp),
                  },
                ],
              };
              return updatedMessage;
            }

            return msg;
          });

          return updated;
        });

        // Forçar re-render para atualizar o status na UI
        setForceUpdate((prev) => prev + 1);
      }
    };
  }, [selectedConversation, setMessages]);

  // Usar o SocketService para registrar o handler
  useEffect(() => {
    const socketInstance = socket.current;
    if (!socketInstance || !socketInstance.connected) {
      return;
    }

    // Registrar o handler através do SocketService
    const handler = (data: any) => {
      if (handleMessageStatusUpdateRef.current) {
        handleMessageStatusUpdateRef.current(data);
      }
    };

    // Configurar o handler no SocketService
    socketService.setEventHandlers({
      onMessageError: (err: any) => {
        const message =
          err.friendlyErrorMessage ||
          (typeof err.error === "string"
            ? err.error
            : "Erro ao enviar mensagem");
        showError(message);
      },
      onMessageStatusUpdate: handler,
    });

    return () => {
      // Remover o handler do SocketService
      socketService.setEventHandlers({
        onMessageError: undefined,
        onMessageStatusUpdate: undefined,
      });
    };
  }, [socket]); // Depender do socket completo

  // Usar o ref para o agrupamento
  const { messageGroups } = useMessageGrouping(messages);

  // Encontrar índice da primeira mensagem não lida e contar quantas são
  const firstUnreadIndex = messages.findIndex(
    (msg) =>
      msg.sentBy === "client" && msg.readStatus && !msg.readStatus.isRead,
  );
  const unreadCount = messages.filter(
    (msg) =>
      msg.sentBy === "client" && msg.readStatus && !msg.readStatus.isRead,
  ).length;

  const currentState = useMemo(
    () => ({
      ...messageState,
      hasConversation: !!selectedConversation,
      isEmpty: messages.length === 0,
      isLoading,
    }),
    [messageState, selectedConversation, messages.length, isLoading],
  );

  // Efeito para marcar mensagens como lidas quando a conversa é aberta (apenas uma vez)
  const hasMarkedAsReadRef = useRef<Set<string>>(new Set());
  const markAllAsReadRef = useRef(markAllAsRead);
  const isProcessingRef = useRef(false);
  const markAsReadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Atualiza o ref quando markAllAsRead mudar
  useEffect(() => {
    if (isMountedRef.current) {
      markAllAsReadRef.current = markAllAsRead;
    }
  }, [markAllAsRead]);

  // Listener para evento de mensagens marcadas como lidas
  useEffect(() => {
    const handleAllMessagesRead = (event: CustomEvent) => {
      const { phoneNumber } = event.detail;
      if (phoneNumber) {
        const currentPhone =
          selectedConversation?.valid_telephone ||
          selectedConversation?.telephone;
        const normalizedEventPhone = normalizePhoneNumber(phoneNumber);
        const normalizedCurrentPhone = normalizePhoneNumber(currentPhone || "");

        if (normalizedEventPhone === normalizedCurrentPhone) {
          // Forçar re-render para atualizar o unreadCount
          setForceUpdate((prev) => prev + 1);

          // Atualizar todas as mensagens do cliente como lidas imediatamente
          setMessages((prev) =>
            prev.map((msg) =>
              msg.sentBy === "client" && msg._id
                ? {
                    ...msg,
                    readStatus: {
                      isRead: true,
                      readBy: {
                        id: account?.id || "",
                        name: account?.username || "",
                      },
                      readAt: new Date(),
                    },
                  }
                : msg,
            ),
          );
        }
      }
    };

    window.addEventListener(
      "allMessagesRead",
      handleAllMessagesRead as (event: Event) => void,
    );

    return () => {
      window.removeEventListener(
        "allMessagesRead",
        handleAllMessagesRead as (event: Event) => void,
      );
    };
  }, [selectedConversation, account]);

  // Definir conversationKey
  const conversationKey =
    selectedConversation?.valid_telephone || selectedConversation?.telephone;

  // useEffect para limpeza quando a conversa muda
  useEffect(() => {
    // Limpar timeout anterior se existir
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
      markAsReadTimeoutRef.current = null;
    }

    // Limpar o hasMarkedAsReadRef quando a conversa muda
    if (conversationKey) {
      hasMarkedAsReadRef.current.clear();
    }
  }, [selectedConversation?.id]); // Mudança: usar selectedConversation?.id em vez de conversationKey

  // useEffect separado para marcar como lida quando mensagens são carregadas
  useEffect(() => {
    if (
      !selectedConversation ||
      !conversationKey ||
      messages.length === 0 ||
      isProcessingRef.current
    ) {
      return;
    }

    // Verificar se já processou esta conversa
    if (hasMarkedAsReadRef.current.has(conversationKey)) {
      return;
    }

    const unreadMessages = messages.filter(
      (message) =>
        message.sentBy === "client" &&
        message._id &&
        !message.readStatus?.isRead,
    );

    if (unreadMessages.length > 0) {
      // Marca como processando para evitar execuções simultâneas
      isProcessingRef.current = true;

      // Aguardar 2 segundos antes de marcar como lida
      markAsReadTimeoutRef.current = setTimeout(() => {
        if (!markAllAsReadRef.current) {
          isProcessingRef.current = false;
          return;
        }

        // Chamar markAllAsRead diretamente
        markAllAsReadRef
          .current()
          .then(() => {
            hasMarkedAsReadRef.current.add(conversationKey);
            isProcessingRef.current = false;
            if (onAllRead) {
              onAllRead();
            }
          })
          .catch((error) => {
            console.error(
              "[DEBUG] ChatMessages - Erro ao marcar como lida:",
              error,
            );
            isProcessingRef.current = false;
            // Usar o sistema centralizado de erro
            showError("Erro ao marcar mensagens como lidas.");
          });
      }, 2000); // 2 segundos de delay
    }
  }, [messages, selectedConversation?.id, onAllRead, markAllAsRead]); // Mudança: usar selectedConversation?.id em vez de conversationKey

  // Cleanup quando o componente for desmontado
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Limpar timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (conversationScrollTimeoutRef.current) {
        clearTimeout(conversationScrollTimeoutRef.current);
      }
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, []);

  if (!selectedConversation) {
    return (
      <Wrapper>
        <EmptyState
          message="Selecione uma conversa para começar"
          showIcon={false}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={wrapperRef}>
      {currentState.loadingState === "initial" && messages.length === 0 && (
        <LoadingStateComponent type="initial" />
      )}
      {currentState.isEmpty && currentState.loadingState === "idle" && (
        <EmptyState />
      )}
      {currentState.hasMore && messages.length > 0 && (
        <Box
          ref={loadMoreRef}
          sx={{
            height: "20px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "20px",
          }}
        >
          {currentState.loadingState === "loading_more" && (
            <LoadingStateComponent type="more" />
          )}
        </Box>
      )}
      {messageGroups.map((group, groupIndex) => {
        // Calcular índice de início para este grupo
        const startIndex = messageGroups
          .slice(0, groupIndex)
          .reduce((acc, g) => acc + g.messages.length, 0);

        return (
          <MessageGroup
            key={`${group.date}-${forceUpdate}`}
            groupDate={group.date}
            messages={group.messages}
            startIndex={startIndex}
            unreadCount={unreadCount}
            firstUnreadIndex={firstUnreadIndex}
            forceUpdate={forceUpdate}
            transformMessage={transformMessage}
            getMessageReadStatus={getMessageReadStatus}
          />
        );
      })}
      <div ref={messagesEndRef} style={{ height: "1px" }} />
    </Wrapper>
  );
};

export default ChatMessages;
